import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Header, SkeletonTable, TableDropdown } from "../components";
import { useStateContext } from "../contexts/ContextProvider";
import { userRequest } from "../utils/requestMethods";
import { MdKeyboardArrowRight, MdFilterList } from "react-icons/md";
import { ImSpinner } from "react-icons/im";
import { FiMoreHorizontal } from "react-icons/fi";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "rgb(248, 250, 252)",
      borderTopLeftRadius: "1rem",
      borderTopRightRadius: "1rem",
      borderBottomWidth: "1px",
      borderBottomColor: "rgb(229, 231, 235)",
      minHeight: "48px",
    },
  },
  rows: {
    style: {
      minHeight: "48px",
    },
  },
  cells: {
    style: {
      paddingTop: "8px",
      paddingBottom: "8px",
    },
  },
};

const Mandates = ({ history }) => {
  const { user, currentColor } = useStateContext();
  const [mandates, setMandates] = useState();
  const [clicked, setClicked] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState("");
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await userRequest.get(`/schools?limit=500&kyc_status=${kycStatus}`);
      setMandates(data.schools);
    } catch (error) {
      console.log(error);
      setError("Failed to fetch schools data");
    } finally {
      setLoading(false);
    }
  }, [kycStatus]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const kycProgressMapping = {
    0: 'Not Started',
    1: 'Details Submitted',
    2: 'Reviewed',
    3: 'Anchor Account Created',
    4: 'Validation In Progress',
    5: 'Awaiting Docs',
    6: 'KYC Validation Failed',
    7: 'KYC Validated',
    8: 'Deposit Account Created'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-red-100 text-red-800";
    }
  };

  const columns = [
    {
      name: "Customer ID",
      selector: (row) => row._id,
      grow: 1,
    },
    {
      name: "School Name",
      selector: (row) => row.school_name,
      grow: 2,
    },
    {
      name: "KYC Status",
      cell: (row) => (
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(row.kyc_status)}`}>
          {row.kyc_status === "approved" ? "Approved" : row.kyc_status === "pending" ? "Pending" : "Not Started"}
        </span>
      ),
      grow: 1,
    },
    {
      name: "KYC Progress",
      selector: (row) => kycProgressMapping[row.kyc_progress],
      grow: 1,
    },
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View KYC Details",
                onClick: () => history.push(`/school/details/${row._id}`),
              },
              {
                label: "Classes Management",
                onClick: () => history.push({ pathname: `/school/classes`, state: { school: row } }),
              },
              {
                label: "Result Management",
                onClick: () => history.push({ pathname: `/school/result-management`, state: { school: row } }),
              },
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "100px",
    },
  ];

  return (
    <div className="m-3 mt-4 p-2 md:p-10 bg-white rounded-2xl dark:bg-secondary-dark-bg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <Header category="Onboarding" title={`Welcome, ${user?.firstname}!`} />
        <button
          type="button"
          onClick={() => setClicked(!clicked)}
          className="inline-flex items-center gap-2 px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-full text-white"
          style={{ backgroundColor: currentColor }}
        >
          <MdFilterList className="text-xl" />
          Filters
        </button>
      </div>

      {clicked && (
        <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="grid gap-6 mb-6 md:grid-cols-4">
            <div>
              <label
                htmlFor="kycStatus"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                KYC Status
              </label>
              <select 
                id="kycStatus" 
                onChange={(e) => setKycStatus(e.target.value)} 
                className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={kycStatus}
              >
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="not_started">Not Started</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
          <i className="fal fa-check mr-2"></i>
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
          <i className="fal fa-exclamation-circle mr-2"></i>
          <span>{error}</span>
        </div>
      )}

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <DataTable
          data={mandates}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={
            <div className="p-4">
              <ImSpinner className="animate-spin text-2xl" style={{ color: currentColor }} />
            </div>
          }
          highlightOnHover
          striped
          responsive
        />
      </div>
    </div>
  );
};

export default Mandates;
